import { PlusOutlined } from '@ant-design/icons';
import {
  Checkbox,
  Col,
  Flex,
  Input,
  Row,
  Spin,
  Upload,
  message,
  notification,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';

import { Entities_Team_AddUserFeedback_ImageDetail } from '@suntex.fim/api-client';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { EntityLevel } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import { Button, Modal, Select, Tooltip } from '@suntex.fim/ui';
import { Search2, Send2 } from '@suntex.fim/ui/icons';

import { MAX_MESSAGE_LENGTH } from '../../constants';
import {
  type GameContent,
  type ModuleName,
  filterByField,
  generateModifiedContent,
  htmlToMarkdown,
  richTextFormats,
} from '../../utils';

import { useCustomPrompt } from './useCustomPrompt';

const MAX_IMAGES = 10;
const MAX_FILE_SIZE_MB = 5;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;

const EMPTY_GAME_CONTENT: GameContent = {};
const EMPTY_LINK_OPTIONS: { label: string; key: string }[] = [];

type SendMessageProps = {
  isModalOpen: boolean;
  closeModal: (success: boolean) => void;
  linkOptions?: { label: string; key: string }[];
  selectedUserOptions: {
    label: string | undefined;
    value: number | undefined;
  }[];
  recipientEntityId?: number;
  isSendPlayerIds?: boolean;
  gameContent?: GameContent;
  mode?: string;
  title?: string;
  sent?: string;
  btntext?: string;
  getMessageContent?: (moduleSlug: string) => ModuleName;
  /** Optional level label to display above the skills list. */
  level?: string;
  reportType?: string;
};

interface FileInput {
  fileName: string;
  /** Required by antd UploadFile */
  name: string;
  url: string;
  uid: string;
}

function transformFilesArray(
  filesArray: FileInput[],
): Entities_Team_AddUserFeedback_ImageDetail[] {
  return filesArray.map((file) => ({
    data: file.url.split(',')[1] ?? '',
    fileName: file.fileName,
  }));
}

function showSuccessToast() {
  notification.success({
    message: undefined,
    duration: 5,
    key: 'send-message-success',
    description: (
      <Flex align="center" className="toast-message__content">
        <span className="text-chalkboard-txt">Message sent successfully.</span>
      </Flex>
    ),
    placement: 'top',
    closeIcon: null,
    className: 'toast-message',
  });
}

function showErrorToast(text = 'Message could not be sent') {
  notification.error({
    message: undefined,
    duration: 5,
    key: 'send-message-error',
    description: (
      <Flex align="center" className="toast-message__content">
        <span className="text-chalkboard-txt">{text}</span>
      </Flex>
    ),
    placement: 'top',
    closeIcon: null,
    className: 'toast-message',
  });
}

/** Modal for composing and sending messages to players or team leaders. */
export function SendMessage({
  level,
  isModalOpen,
  closeModal,
  linkOptions = EMPTY_LINK_OPTIONS,
  selectedUserOptions,
  recipientEntityId,
  isSendPlayerIds = true,
  gameContent = EMPTY_GAME_CONTENT,
  mode = '',
  title,
  sent,
  btntext,
  getMessageContent,
  reportType,
}: SendMessageProps) {
  const { api } = useFramework();
  const { login } = useAuth(isAuthenticated);
  const quillRef = useRef<ReactQuill>(null);
  const { openPrompt, CustomPrompt } = useCustomPrompt();

  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<FileInput[]>([]);
  const [selectedLinks, setSelectedLinks] = useState<Record<string, boolean>>(
    {},
  );
  const [subject, setSubject] = useState('');
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<readonly number[]>(
    [],
  );
  const [searchByFullName, setSearchByFullName] = useState('');

  const filteredLinkOptions = useMemo(
    () => filterByField(linkOptions, searchByFullName, 'label'),
    [linkOptions, searchByFullName],
  );

  useEffect(() => {
    setSelectedPlayerIds(selectedUserOptions.map(({ value }) => value ?? 0));
  }, [selectedUserOptions]);

  useEffect(() => {
    if (title && sent && btntext) {
      setContent(window.location.href);
    }
  }, [isModalOpen, title, sent, btntext]);

  const handleChangeSelectLink = (
    key: string,
    value: boolean,
    levelLabel: string,
    operation: string,
  ) => {
    const label = `${reportType ? `${reportType}:` : ''}${levelLabel ? `${levelLabel}-` : ''}${operation}`;
    const newSelectedLinks = { ...selectedLinks, [key]: value };
    setSelectedLinks(newSelectedLinks);

    const editor = quillRef.current?.getEditor();
    const currentContent = editor?.getText() ?? '';

    if (!value) {
      const labelIndex = currentContent.indexOf(label);
      const NOT_FOUND = -1;
      if (labelIndex !== NOT_FOUND) {
        let deleteStart = labelIndex;
        let deleteLength = label.length;
        if (labelIndex > 0 && currentContent[labelIndex - 1] === '\n') {
          deleteStart = labelIndex - 1;
          deleteLength += 1;
        } else if (
          labelIndex + label.length < currentContent.length &&
          currentContent[labelIndex + label.length] === '\n'
        ) {
          deleteLength += 1;
        }
        editor?.deleteText(deleteStart, deleteLength);
      }
    } else {
      const insertIndex = editor?.getLength() ?? 0;
      const textToInsert = currentContent.length > 0 ? `\n${label}` : label;
      const formatStart =
        currentContent.length > 0 ? insertIndex + 1 : insertIndex;
      editor?.insertText(insertIndex, textToInsert, 'user');
      editor?.formatText(formatStart, label.length, 'link', key, 'user');
    }
  };

  const messageMarkdown = useMemo(() => htmlToMarkdown(content), [content]);
  const markdownLength = messageMarkdown.length;

  const sendMessage = async () => {
    const isFeedbackMode = !!(title && sent && btntext);
    setIsLoading(true);
    try {
      if (isFeedbackMode) {
        // Feedback submission (with image upload)
        await api.post_Team_AddUserFeedback({
          loginId: login.id,
          feedbackDetails: {
            subject,
            message: messageMarkdown,
            images: transformFilesArray(uploadedImages),
          },
        });
        closeModal(true);
        showSuccessToast();
        setUploadedImages([]);
      } else if (
        selectedPlayerIds.length === 0 &&
        login.entityLevel === EntityLevel.Player
      ) {
        // Player sending directly to team leader
        await api.post_Notification_InsertDirect({
          entityId: login.entityId,
          receiverLoginId: 0, // server resolves to team leader
          senderLoginId: login.id,
          message: messageMarkdown,
          type: 'custom',
          subject,
          hash: undefined,
          receiverName: undefined,
        });
        message.success('Message sent successfully');
        closeModal(true);
      } else {
        // Teacher / admin sending to players
        await api.post_Notification_InsertTeam({
          entityId: recipientEntityId ?? login.entityId,
          receiverLoginId: 0,
          receiverName: '',
          senderLoginId: login.id,
          subject,
          message: messageMarkdown,
          hash: '',
          type: 'custom',
          playerIds: isSendPlayerIds ? [...selectedPlayerIds] : [],
        });
        message.success('Message sent successfully');
        closeModal(true);
        showSuccessToast();
      }
    } catch {
      showErrorToast();
    } finally {
      setIsLoading(false);
      setSelectedLinks({});
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      setContent('');
      setSubject('');
      return;
    }
    if (!title && !sent && !btntext && getMessageContent) {
      setContent(generateModifiedContent(gameContent, getMessageContent));
    }
  }, [isModalOpen, mode, gameContent, title, sent, btntext, getMessageContent]);

  const handleChangeContent = (newContent: string) => {
    setContent(newContent.split('<p><br></p>').join(''));
  };

  const disableSendButton = () => {
    const isFeedbackMode = !!(title && sent && btntext);
    if (isFeedbackMode) return isLoading;
    if (isLoading || !markdownLength || markdownLength > MAX_MESSAGE_LENGTH)
      return true;
    if (login.entityLevel === EntityLevel.Player) return false;
    return !selectedPlayerIds.length;
  };

  const handleImageUpload = useCallback(async () => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection();
    const value = await openPrompt('Please Enter Image Url:');
    if (value && range) {
      editor.insertEmbed(range.index, 'image', value, 'user');
    }
  }, [openPrompt]);

  const imageHandlerRef = useRef(handleImageUpload);
  imageHandlerRef.current = handleImageUpload;

  const toolbarModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic'],
          [{ header: '1' }, { header: '2' }, { font: [] }],
          [{ size: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['image'],
        ],
        handlers: { image: () => void imageHandlerRef.current() },
      },
      clipboard: { matchVisual: false },
    }),
    [],
  );

  const disableCursor = useCallback(
    (event: { key: string; preventDefault: () => void }) => {
      if (mode.length === 0) {
        if (markdownLength >= MAX_MESSAGE_LENGTH && event.key !== 'Backspace') {
          event.preventDefault();
        }
      }
    },
    [mode, markdownLength],
  );

  const handleUpload = useCallback((file: File & { uid?: string }) => {
    const imageType = file.type.split('/')[1];
    const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'jfif'];
    if (!allowedTypes.includes(imageType ?? '')) {
      void message.error(
        'Please select an image of type JPG, JPEG, PNG, GIF, or JFIF.',
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size / BYTES_PER_MB > MAX_FILE_SIZE_MB) {
      void message.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setUploadedImages((prev) =>
        prev.length < MAX_IMAGES
          ? [
              ...prev,
              {
                fileName: file.name,
                name: file.name,
                url,
                uid: file.uid ?? '',
              },
            ]
          : prev,
      );
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection();
      if (range) {
        editor?.insertEmbed(range.index, 'image', url);
      }
    };
    reader.readAsDataURL(file);
    return false;
  }, []);

  const handleRemoveImage = useCallback(
    (file: { uid: string; url: string }) => {
      setUploadedImages((prev) => prev.filter((img) => img.uid !== file.uid));
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const index = editor.getText().search(file.url);
        editor.deleteText(index, file.url.length);
      }
    },
    [],
  );

  return (
    <>
      {isModalOpen ? (
        <Modal
          closable={false}
          centered
          title={title ?? 'Send a Message'}
          open
          onOk={() => closeModal(false)}
          onCancel={() => closeModal(false)}
          width={900}
          className="email"
          wrapClassName="email-modal-wrap"
          footer={
            <>
              <Button
                className="message-modal-cancel-btn"
                shape="round"
                intent="secondary"
                key="back"
                onClick={() => {
                  setUploadedImages([]);
                  setSelectedLinks({});
                  closeModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                className="message-modal-cancel-btn"
                intent="primary"
                icon={<Send2 />}
                iconPosition="end"
                onClick={() => void sendMessage()}
                disabled={disableSendButton()}
              >
                {btntext ?? 'Send Message'} {isLoading ? <Spin /> : null}
              </Button>
            </>
          }
        >
          {!(title && sent && btntext) && (
            <Flex gap={4} className="section-to" align="center">
              <label htmlFor="to" className="ant-form-item-no-colon">
                {sent ?? 'To:'}
              </label>
              <Select.Tags
                allowClear
                id="to"
                value={selectedPlayerIds}
                onChange={(value) => setSelectedPlayerIds(value)}
                options={selectedUserOptions.map((o) => ({
                  label: o.label ?? '',
                  value: o.value ?? 0,
                }))}
                className="send-msg"
                popupClassName="send-msg"
                style={{ width: '100%' }}
              />
            </Flex>
          )}

          {!!(title && sent && btntext) && (
            <Flex gap={4} className="section-to" align="center">
              <label htmlFor="subject" className="ant-form-item-no-colon">
                Subject:
              </label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ width: '100%' }}
              />
            </Flex>
          )}

          <Row className="section-email">
            {linkOptions.length > 0 ? (
              <Col flex="273px" className="section-email__skills-container">
                <div className="search-bar">
                  <Input
                    placeholder="Search Skills..."
                    prefix={<Search2 />}
                    onChange={(e) => setSearchByFullName(e.target.value)}
                    aria-label="Search skills"
                  />
                </div>
                <ul className="skills">
                  {level && <h5 className="fw-bold">{level}</h5>}
                  {filteredLinkOptions.map(({ label, key }) => (
                    <li key={key} className="skillset">
                      <div className="skillset__label">{label}</div>
                      <div className="skillset__chkbox">
                        <Checkbox
                          checked={selectedLinks[key] ?? false}
                          onChange={(e) =>
                            handleChangeSelectLink(
                              key,
                              e.target.checked,
                              level ?? '',
                              label,
                            )
                          }
                          aria-label={label}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </Col>
            ) : null}

            <Col
              flex="1"
              className="section-email__email-container upload-image"
            >
              {title && sent && btntext && (
                <Upload
                  multiple
                  customRequest={({ file }) => void handleUpload(file as File)}
                  onRemove={(file) =>
                    handleRemoveImage(file as { uid: string; url: string })
                  }
                  fileList={uploadedImages}
                  maxCount={MAX_IMAGES}
                  listType="picture-card"
                  showUploadList={{
                    showPreviewIcon: false,
                    showRemoveIcon: true,
                  }}
                >
                  {uploadedImages.length < MAX_IMAGES ? (
                    <>
                      <PlusOutlined />
                      <div>Upload</div>
                      <p>
                        Drag or click to upload images (Max {MAX_IMAGES} images,
                        5MB each)
                      </p>
                    </>
                  ) : null}
                </Upload>
              )}
              <div className="richtext-wrapper">
                <div className="richtext-editor">
                  <ReactQuill
                    onKeyDown={disableCursor}
                    ref={quillRef}
                    onChange={handleChangeContent}
                    value={content}
                    modules={toolbarModules}
                    formats={[...richTextFormats]}
                  />
                  {CustomPrompt}
                  <Flex
                    className="richtext-info text-chalkboard-txt"
                    justify="space-between"
                  >
                    {mode.length === 0 && (
                      <Tooltip title="Formatting may add to your character count.">
                        <span className="richtext-info__desc">
                          Used <span className="fw-bold">{markdownLength}</span>{' '}
                          of{' '}
                          <span className="fw-bold">{MAX_MESSAGE_LENGTH}</span>{' '}
                          characters.
                        </span>
                      </Tooltip>
                    )}
                  </Flex>
                </div>
              </div>
            </Col>
          </Row>
        </Modal>
      ) : null}
    </>
  );
}
